/**
 * Casca dos modais do painel do Cabal Next: o mesmo visual do "Inscrever Beta" (classes nxb-, beta.css), num portal
 * no <body>. Esc / clique fora fecham (menos com `busy`).
 */
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import '@/features/Home/next/beta.css';

type Props = { open: boolean; onClose: () => void; busy?: boolean; className?: string; children: ReactNode };

export function NxModal({ open, onClose, busy = false, className, children }: Props) {
  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const prev = body.style.overflow;
    body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="nxb-overlay" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className={`nxb-modal${className ? ` ${className}` : ''}`} role="dialog" aria-modal="true">
        <button type="button" className="nxb-close" onClick={onClose} aria-label="Fechar" disabled={busy}>
          <X size={20} />
        </button>
        {children}
      </div>
    </div>,
    document.body
  );
}

/** O erro da API chega como {detail: {Status, Message}}, {detail: "texto"} ou {message}. */
export function errMsg(e: any, fallback: string): string {
  const b = e?.detail && typeof e.detail === 'object' && !Array.isArray(e.detail) ? e.detail : e;
  if (Array.isArray(e?.detail)) return fallback;   // 422 do FastAPI (lista de campos)
  return String(b?.Message || b?.message || (typeof e?.detail === 'string' ? e.detail : '') || fallback);
}

const DATE = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const DATE_TIME = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const fmtDate = (s?: string | null) => (s ? DATE.format(new Date(s)) : '—');
export const fmtDateTime = (s?: string | null) => (s ? DATE_TIME.format(new Date(s)) : '—');
